<template>
    <DialogLayout :class-name="'candidate-status-options-dialog'" @close="handleClose">
        <template #title>{{ t('employee.statusOptions.title') }}</template>
        <template #default>
            <div class="status-options-header">
                <span class="header-tip">{{ t('employee.statusOptions.tip') }}</span>
                <button class="add-btn" @click="handleAdd">
                    <img src="@/assets/icons/add.svg" alt="add" class="add-icon" />
                    {{ t('employee.statusOptions.add') }}
                </button>
            </div>

            <div v-loading="loading" class="status-options-table">
                <div class="table-head">
                    <span>{{ t('employee.statusOptions.columnName') }}</span>
                    <span>{{ t('employee.statusOptions.columnRefCount') }}</span>
                    <span>{{ t('employee.statusOptions.columnAction') }}</span>
                </div>
                <div class="table-body">
                    <div v-for="row in rows" :key="row.key" class="table-row">
                        <div class="cell-name">
                            <!-- 编辑态：失焦时提交，内容为空则保持编辑并提示 -->
                            <el-input v-if="row.editing" :ref="(el: any) => setInputRef(el, row.key)"
                                v-model="row.name" :placeholder="t('employee.statusOptions.namePlaceholder')"
                                :maxlength="20" :class="{ 'is-invalid': !!row.error }" @blur="handleBlur(row)"
                                @keyup.enter="handleBlur(row)" />
                            <template v-else>
                                <span class="option-name">{{ row.name }}</span>
                                <img src="@/assets/icons/edit2.svg" alt="edit" class="edit-icon"
                                    @click="handleEdit(row)" />
                            </template>
                            <div v-if="row.error" class="cell-error">{{ row.error }}</div>
                        </div>
                        <div class="cell-count">{{ row.refCount }}</div>
                        <div class="cell-action">
                            <!-- mousedown.prevent：点删除时不让输入框失焦，避免先走 blur 提交 -->
                            <img src="@/assets/icons/delete.svg" alt="delete"
                                :class="['delete-icon', { 'is-disabled': !canDelete(row) }]" @mousedown.prevent
                                @click="canDelete(row) && handleDelete(row)" />
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleClose">
                {{ t('employee.statusOptions.close') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, nextTick, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import DialogLayout from './dialog-layout.vue'
import type { CandidateStatusOption, CandidateStatusOptionsDialogProps } from './candidate-status-options-dialog-types'
import {
    getCandidateStatusOptionsApi,
    createCandidateStatusOptionApi,
    renameCandidateStatusOptionApi,
    deleteCandidateStatusOptionApi,
} from '@/api/candidate'
const { t } = useI18n()

const props = defineProps<CandidateStatusOptionsDialogProps>()

const emit = defineEmits<{
    (e: 'confirm', options: CandidateStatusOption[]): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

interface StatusOptionRow extends CandidateStatusOption {
    /** 列表 key：新增行还没有后端 id，用它保证渲染与 ref 稳定 */
    key: string
    editing: boolean
    error: string
    /** 是否是尚未提交过的新增行 */
    isNew: boolean
}

const rows = ref<StatusOptionRow[]>([])
const loading = ref(false)
let tempKeySeed = 0

const toRow = (option: CandidateStatusOption): StatusOptionRow => ({
    ...option,
    key: `option-${option.id}`,
    editing: false,
    error: '',
    isNew: false,
})

/** 拉取选项列表 */
const fetchOptions = async () => {
    loading.value = true
    try {
        const list = await getCandidateStatusOptionsApi()
        rows.value = (list || []).map(toRow)
    } catch (error) {
        console.error('获取候选人状态选项失败:', error)
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    // 先用外部已有的列表渲染，避免打开时闪空；随后以接口返回为准
    rows.value = (props.options || []).map(toRow)
    fetchOptions()
})

// 输入框 ref：编辑/新增时需要自动聚焦
const inputRefs = new Map<string, { focus: () => void }>()
const setInputRef = (el: { focus: () => void } | null, key: string) => {
    if (el) {
        inputRefs.set(key, el)
    } else {
        inputRefs.delete(key)
    }
}

const focusRow = async (key: string) => {
    await nextTick()
    inputRefs.get(key)?.focus()
}

/** 添加选项：插入到表格第一行并自动聚焦，失焦校验通过后才提交 */
const handleAdd = () => {
    tempKeySeed += 1
    const key = `new-${tempKeySeed}`
    rows.value.unshift({
        id: 0,
        name: '',
        refCount: 0,
        key,
        editing: true,
        error: '',
        isNew: true,
    })
    focusRow(key)
}

const handleEdit = (row: StatusOptionRow) => {
    row.editing = true
    focusRow(row.key)
}

/** 失焦提交：非空 + 不能与其他选项重名，校验不通过时保持编辑态 */
const handleBlur = async (row: StatusOptionRow) => {
    const name = row.name.trim()
    if (!name) {
        row.error = t('employee.statusOptions.nameRequired')
        return
    }
    const duplicated = rows.value.some(item => item.key !== row.key && item.name.trim() === name)
    if (duplicated) {
        row.error = t('employee.statusOptions.nameDuplicated')
        return
    }
    row.name = name
    row.error = ''

    try {
        if (row.isNew) {
            await createCandidateStatusOptionApi({ name })
        } else {
            await renameCandidateStatusOptionApi({ id: row.id, name })
        }
        row.editing = false
        row.isNew = false
        ElMessage.success(t('common.saveSuccess'))
        // 新增需要拿到后端生成的 id，重命名也顺便同步引用次数
        await fetchOptions()
    } catch (error: unknown) {
        // 提交失败时保持编辑态，方便直接修改后重试
        const message = error && typeof error === 'object' && 'message' in error
            ? String((error as Record<string, unknown>).message)
            : t('common.saveFailed')
        row.error = message
        ElMessage.error(message)
    }
}

/**
 * 可删除的条件：
 * - 引用次数为 0
 * - 内容为空（新增行还没填内容，直接删掉即可，不需要提交）
 *   或内容已通过校验
 */
const canDelete = (row: StatusOptionRow) =>
    row.refCount <= 0 && (!row.name.trim() || !row.error)

const handleDelete = async (row: StatusOptionRow) => {
    // 还没提交过的新增行：直接本地删掉，不调接口
    if (row.isNew) {
        rows.value = rows.value.filter(item => item.key !== row.key)
        inputRefs.delete(row.key)
        return
    }

    try {
        await deleteCandidateStatusOptionApi(row.id)
        rows.value = rows.value.filter(item => item.key !== row.key)
        inputRefs.delete(row.key)
        ElMessage.success(t('common.deleteSuccess'))
    } catch (error: unknown) {
        const message = error && typeof error === 'object' && 'message' in error
            ? String((error as Record<string, unknown>).message)
            : t('common.failed')
        ElMessage.error(message)
    }
}

// 关闭时把最终的选项列表回传给调用方，用于刷新下拉选项
const handleClose = () => {
    emit('confirm', rows.value
        .filter(row => row.name.trim() && !row.isNew)
        .map(({ id, name, refCount }) => ({ id, name, refCount })))
}
</script>

<style scoped lang="scss">
.status-options-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;

    .header-tip {
        font-size: 14px;
        color: #88929B;
    }

    .add-btn {
        display: flex;
        align-items: center;
        gap: 4px;
        padding: 0;
        border: none;
        background: transparent;
        color: var(--el-color-primary);
        font-size: 14px;
        cursor: pointer;

        .add-icon {
            width: 14px;
            height: 14px;
            filter: brightness(0) saturate(100%) invert(37%) sepia(73%) saturate(2234%) hue-rotate(237deg) brightness(97%) contrast(93%);
        }
    }
}

.status-options-table {
    border-radius: 8px;
    overflow: hidden;

    .table-head,
    .table-row {
        display: grid;
        grid-template-columns: 1fr 120px 80px;
        align-items: center;
        column-gap: 12px;
        padding: 0 16px;
    }

    .table-head {
        height: 44px;
        background: #F7F8FA;
        font-size: 14px;
        color: #86909C;
    }

    .table-body {
        max-height: 280px;
        overflow-y: auto;
    }

    .table-row {
        min-height: 46px;
        font-size: 14px;
        color: #1D2129;

        .cell-name {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            padding: 8px 0;

            .option-name {
                word-break: break-word;
            }

            .edit-icon {
                width: 14px;
                height: 14px;
                cursor: pointer;
                filter: brightness(0) saturate(100%) invert(37%) sepia(73%) saturate(2234%) hue-rotate(237deg) brightness(97%) contrast(93%);
            }

            .cell-error {
                width: 100%;
                font-size: 12px;
                color: var(--el-color-danger);
            }

            :deep(.is-invalid .el-input__wrapper) {
                box-shadow: 0 0 0 1px var(--el-color-danger) inset;
            }
        }

        .cell-count {
            color: #1D2129;
        }

        .cell-action {
            .delete-icon {
                width: 16px;
                height: 16px;
                cursor: pointer;
                filter: brightness(0) saturate(100%) invert(36%) sepia(93%) saturate(7471%) hue-rotate(357deg) brightness(96%) contrast(115%);

                // 引用次数 > 0 或校验不通过：置灰不可点击
                &.is-disabled {
                    cursor: not-allowed;
                    filter: brightness(0) saturate(100%) invert(85%) sepia(6%) saturate(200%) hue-rotate(174deg) brightness(95%) contrast(88%);
                }
            }
        }
    }
}
</style>

<style lang="scss">
.candidate-status-options-dialog {
    width: 560px !important;

    .dialog-footer {
        display: flex;
        justify-content: flex-end;

        .el-button {
            min-width: 80px;
        }
    }
}
</style>
